import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import DashboardLayout from "@/layouts/DashboardLayout";
import Header from "@/components/ui/header";
import PageHeader from "@/components/ui/page-header";
import Table from "@/components/ui/table";
import InfoCard from "@/components/ui/info-card";
import Chip from "@/components/ui/chip";
import TicketCategoryCell from "@/features/support-tickets/components/TicketCategoryCell";
import Toast from "@/components/ui/toast";
import { Tickets, Pending, Tick, Eye as EyeIcon } from "@/components/icons";
import type { FilterField, SortOption, TableColumn } from "@/types/component";
import type {
  SupportTicketListItem,
  SupportTicketSortByValue,
  TicketStatusBackend,
} from "@/features/support-tickets";
import {
  SupportTicketSortBy,
  SUPPORT_TICKET_STATUS_CHIP_TYPE,
  SUPPORT_TICKET_STATUS_LABELS_TR,
} from "@/features/support-tickets";
import { clientSupportTicketsApi } from "../services/client-support-tickets.api";
import ClientSupportTicketDetailModal from "../components/ClientSupportTicketDetailModal";
import NewClientSupportTicketModal from "../components/NewClientSupportTicketModal";
import { usePermission } from "@/core/permissions/hooks";
import { PERMISSIONS } from "@/core/permissions/config";
import styles from "./ClientSupportTicketsPage.module.scss";

function formatTicketDate(iso: string): string {
  const d = new Date(iso);
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  return `${day}.${month}.${year}`;
}

function parseDateRangeValue(value: unknown): {
  dateFrom?: string;
  dateTo?: string;
} {
  if (!value || !Array.isArray(value)) return {};
  const [start, end] = value as [Date | null, Date | null];
  const toISO = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
      d.getDate(),
    ).padStart(2, "0")}`;
  return {
    dateFrom: start ? toISO(start) : undefined,
    dateTo: end ? toISO(end) : undefined,
  };
}

const ClientSupportTicketsPage: React.FC = () => {
  const { t } = useTranslation();
  const { hasPermission } = usePermission();
  const canView = hasPermission(PERMISSIONS.SUPPORT_TICKETS_VIEW);
  const canCreate = hasPermission(PERMISSIONS.SUPPORT_TICKETS_CREATE);

  const [tickets, setTickets] = useState<SupportTicketListItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalRecords, setTotalRecords] = useState(0);
  const [stats, setStats] = useState({
    totalCount: 0,
    pendingCount: 0,
    resolvedCount: 0,
  });
  const [filterValues, setFilterValues] = useState<Record<string, any>>({});
  const [sortValue, setSortValue] = useState<SupportTicketSortByValue>(
    SupportTicketSortBy.CREATED_AT_DESC,
  );
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [selectedTicketId, setSelectedTicketId] = useState<string | null>(null);
  const [newTicketOpen, setNewTicketOpen] = useState(false);
  const [toastState, setToastState] = useState<{
    variant: "success" | "fail";
    message: string;
    title?: string;
  } | null>(null);

  // Search debounce 500ms
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setCurrentPage(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const fetchTickets = useCallback(async () => {
    if (!canView) {
      setTickets([]);
      setTotalRecords(0);
      setStats({ totalCount: 0, pendingCount: 0, resolvedCount: 0 });
      setIsLoading(false);
      return;
    }
    const dateRange = parseDateRangeValue(filterValues.dateRange);
    setIsLoading(true);
    try {
      const statusParam = (() => {
        const s = filterValues.status;
        if (s == null || s === "") return undefined;
        const num = Number(s);
        return num === 2 || num === 3
          ? (num as TicketStatusBackend)
          : undefined;
      })();
      const [listRes, statsRes] = await Promise.all([
        clientSupportTicketsApi.getAll({
          page: currentPage,
          limit: Math.min(pageSize, 100),
          search: debouncedSearch || undefined,
          status: statusParam,
          dateFrom: dateRange.dateFrom,
          dateTo: dateRange.dateTo,
          sortBy: sortValue,
        }),
        clientSupportTicketsApi.getSummary(),
      ]);
      setTickets(listRes.data);
      setTotalRecords(listRes.total);
      setStats(statsRes);
    } catch (error) {
      console.error("Error fetching client support tickets:", error);
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, pageSize, debouncedSearch, filterValues, sortValue, canView]);

  useEffect(() => {
    fetchTickets();
  }, [fetchTickets]);

  const handleSearch = (value: string) => {
    setSearchTerm(value);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  const handleFilterChange = (filters: Record<string, any>) => {
    setFilterValues(filters);
    setCurrentPage(1);
  };

  const handleSortChange = (option: SortOption) => {
    if (option?.value) setSortValue(option.value as SupportTicketSortByValue);
    setCurrentPage(1);
  };

  const sortOptions: SortOption[] = [
    {
      label: t("clientSupportTickets.sort.answeredFirst"),
      value: SupportTicketSortBy.ANSWERED_FIRST,
    },
    {
      label: t("clientSupportTickets.sort.pendingFirst"),
      value: SupportTicketSortBy.PENDING_FIRST,
    },
    {
      label: t("clientSupportTickets.sort.createdAtDesc"),
      value: SupportTicketSortBy.CREATED_AT_DESC,
    },
    {
      label: t("clientSupportTickets.sort.createdAtAsc"),
      value: SupportTicketSortBy.CREATED_AT_ASC,
    },
  ];

  const filterFields: FilterField[] = [
    {
      name: "status",
      label: t("clientSupportTickets.filters.status"),
      type: "dropdown",
      placeholder: t("clientSupportTickets.filters.statusPlaceholder"),
      options: [
        { label: SUPPORT_TICKET_STATUS_LABELS_TR[2], value: "2" },
        { label: SUPPORT_TICKET_STATUS_LABELS_TR[3], value: "3" },
      ],
    },
    {
      name: "dateRange",
      label: t("clientSupportTickets.filters.dateRange"),
      type: "daterange",
      placeholder: t("clientSupportTickets.filters.datePlaceholder"),
    },
  ];

  const handleViewDetail = (row: SupportTicketListItem) => {
    setSelectedTicketId(row.id);
    setDetailModalOpen(true);
  };

  const columns: TableColumn<SupportTicketListItem>[] = [
    {
      header: t("clientSupportTickets.columns.ticketNo"),
      accessor: "requestNo",
      className: "ticket-no-column",
    },
    {
      header: t("clientSupportTickets.columns.createdAt"),
      accessor: (row) => formatTicketDate(row.createdAt),
      className: "created-at-column",
    },
    {
      header: t("clientSupportTickets.columns.education"),
      accessor: (row) => row.education?.name ?? "—",
      className: "education-column",
    },
    {
      header: t("supportTicketCategory.column"),
      accessor: (row) => (
        <TicketCategoryCell category={row.category} approvalStatus={row.approvalStatus} />
      ),
      className: "category-column",
    },
    {
      header: t("clientSupportTickets.columns.status"),
      accessor: (row) => (
        <Chip
          type={SUPPORT_TICKET_STATUS_CHIP_TYPE[row.status]}
          size="small"
          variant="filled"
        >
          {SUPPORT_TICKET_STATUS_LABELS_TR[row.status]}
        </Chip>
      ),
      className: "status-column",
    },
  ];

  const renderActions = (row: SupportTicketListItem) => (
    <button
      type="button"
      className={styles.viewButton}
      onClick={(e) => {
        e.stopPropagation();
        handleViewDetail(row);
      }}
      aria-label={t("common.view")}
    >
      <EyeIcon width={20} height={20} />
    </button>
  );

  return (
    <DashboardLayout>
      {toastState && (
        <div className={styles.toastWrapper}>
          <Toast
            variant={toastState.variant}
            message={toastState.message}
            title={toastState.title}
            onClose={() => setToastState(null)}
          />
        </div>
      )}
      <Header title={t("clientSupportTickets.title")} />

      <div className={styles.infoCardsGrid}>
        <InfoCard
          title={t("clientSupportTickets.stats.total")}
          value={stats.totalCount}
          icon={<Tickets width={24} height={24} color="#fff" />}
        />
        <InfoCard
          title={t("clientSupportTickets.stats.pending")}
          value={stats.pendingCount}
          icon={<Pending width={24} height={24} color="#fff" />}
        />
        <InfoCard
          title={t("clientSupportTickets.stats.resolved")}
          value={stats.resolvedCount}
          icon={<Tick width={24} height={24} color="#fff" />}
        />
      </div>

      <PageHeader
        searchPlaceholder={t("clientSupportTickets.searchPlaceholder")}
        searchValue={searchTerm}
        searchMaxLength={200}
        onSearch={handleSearch}
        showSort
        sortOptions={sortOptions}
        selectedSortValue={sortValue}
        onSort={handleSortChange}
        filters={filterFields}
        onFilterChange={handleFilterChange}
        onAddNew={canCreate ? () => setNewTicketOpen(true) : undefined}
        addButtonLabel={
          canCreate
            ? t("clientSupportTickets.newTicket.button")
            : undefined
        }
      />

      <Table
        columns={columns}
        data={tickets}
        isLoading={isLoading}
        currentPage={currentPage}
        pageSize={pageSize}
        totalRecords={totalRecords}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        renderActions={renderActions}
        emptyMessage={t("clientSupportTickets.noData")}
      />

      <ClientSupportTicketDetailModal
        ticketId={selectedTicketId}
        isOpen={detailModalOpen}
        onClose={() => setDetailModalOpen(false)}
      />

      <NewClientSupportTicketModal
        isOpen={newTicketOpen}
        onClose={() => setNewTicketOpen(false)}
        onSuccess={() => {
          setToastState({
            variant: "success",
            title: t("clientSupportTickets.newTicket.toastTitle"),
            message: t("clientSupportTickets.newTicket.toastSuccess"),
          });
          fetchTickets();
        }}
      />
    </DashboardLayout>
  );
};

export default ClientSupportTicketsPage;
